'use client';

import { useCallback, useMemo } from 'react';

import { useConfig, useConfigValue } from '@/components/providers/config-provider';
import { usePlatform, type Platform } from '@/components/providers/platform-provider';
import { useAffiliateTracking } from '@/hooks/use-affiliate-tracking';
import type { AffiliateConfig, LinkQueryParamsConfig } from '@/lib/types/tenant';
import {
  AFFILIATE_REDIRECT_PARAM,
  getTargetParam,
  shouldApplyAffiliate,
} from '@/lib/utils/affiliate';
import {
  applyQueryParams,
  hostnamesMatch,
  interpolateTemplate,
  matchesAnyPattern,
  stripWww,
} from '@/lib/utils/domain';

function resolveRuleParams({
  params,
  tokens,
}: {
  params: Record<string, string>;
  tokens: Record<string, string>;
}): Record<string, string> {
  const entries = Object.entries(params);
  if (!entries.some(([, value]) => value.includes('{'))) return params;

  const resolved: Record<string, string> = {};
  for (const [key, value] of entries) {
    resolved[key] = value.includes('{') ? interpolateTemplate({ template: value, tokens }) : value;
  }
  return resolved;
}

function applyLinkQueryParamsToUrl({
  url,
  linkQueryParams,
  platform,
}: {
  url: string;
  linkQueryParams: LinkQueryParamsConfig | undefined;
  platform: Platform;
}): string {
  if (!linkQueryParams?.rules?.length) return url;

  const tokens = { platform };
  let result = url;
  for (const rule of linkQueryParams.rules) {
    if (matchesAnyPattern({ url, patterns: rule.pattern })) {
      result = applyQueryParams({
        url: result,
        params: resolveRuleParams({ params: rule.params, tokens }),
      });
    }
  }
  return result;
}

interface ReplaceHostnameParams {
  url: string;
  prodBaseUrl: string;
  envBaseUrl: string;
}

function replaceHostname({ url, prodBaseUrl, envBaseUrl }: ReplaceHostnameParams): string | null {
  try {
    const urlObj = new URL(url);
    const prodUrlObj = new URL(prodBaseUrl);
    const envUrlObj = new URL(envBaseUrl);

    if (hostnamesMatch({ a: urlObj.hostname, b: prodUrlObj.hostname })) {
      urlObj.hostname = envUrlObj.hostname;
      urlObj.protocol = envUrlObj.protocol;
      urlObj.port = envUrlObj.port || '';
      return urlObj.href;
    }
    return null;
  } catch {
    return null;
  }
}

function isOwnRedirectUrl({ url, appOrigins }: { url: string; appOrigins: string[] }): boolean {
  if (!url.includes('/redirect/')) return false;
  try {
    const urlObj = new URL(url);
    if (!urlObj.pathname.startsWith('/redirect/')) return false;
    if (appOrigins.length === 0) return true;
    return appOrigins.includes(urlObj.origin);
  } catch {
    return false;
  }
}

function toOrigin(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

function replaceHostnameInString({ url, prodBaseUrl, envBaseUrl }: ReplaceHostnameParams): string {
  try {
    const prodHostname = stripWww(new URL(prodBaseUrl).hostname);
    const envHostname = new URL(envBaseUrl).hostname;

    if (url.includes(`www.${prodHostname}`)) {
      return url.replace(`www.${prodHostname}`, envHostname);
    }
    if (url.includes(prodHostname)) {
      return url.replace(prodHostname, envHostname);
    }
  } catch {
    // Invalid base URLs
  }
  return url;
}

export function useEnvironmentUrl() {
  const { environmentUrls } = useConfig();
  const linkQueryParams = useConfigValue<LinkQueryParamsConfig>('navigation.linkQueryParams');
  const affiliateConfig = useConfigValue<AffiliateConfig>('navigation.affiliateConfig');
  const affiliateId = useAffiliateTracking();
  const platform = usePlatform();

  const { production, current } = environmentUrls || {};

  const shouldReplace = Boolean(production && current && production !== current);

  const replaceUrl = useCallback(
    (url: string | undefined | null): string | undefined | null => {
      if (!url || !shouldReplace || !production || !current) {
        return url;
      }

      return (
        replaceHostname({ url, prodBaseUrl: production, envBaseUrl: current }) ||
        replaceHostnameInString({ url, prodBaseUrl: production, envBaseUrl: current })
      );
    },
    [shouldReplace, production, current],
  );

  const appOrigins = useMemo(
    () => [toOrigin(production), toOrigin(current)].filter((o): o is string => o !== null),
    [production, current],
  );

  const processUrl = useCallback(
    (url: string | undefined | null): string => {
      if (!url) return '';

      if (isOwnRedirectUrl({ url, appOrigins })) {
        if (!affiliateId) return url;
        return applyQueryParams({
          url,
          params: { [AFFILIATE_REDIRECT_PARAM]: affiliateId },
        });
      }

      const replaced = replaceUrl(url) ?? url;
      let result = applyLinkQueryParamsToUrl({
        url: replaced,
        linkQueryParams: linkQueryParams ?? undefined,
        platform,
      });

      if (
        affiliateId &&
        shouldApplyAffiliate({ url: result, config: affiliateConfig ?? undefined })
      ) {
        result = applyQueryParams({
          url: result,
          params: { [getTargetParam(affiliateConfig!)]: affiliateId },
        });
      }

      return result;
    },
    [replaceUrl, linkQueryParams, affiliateId, affiliateConfig, appOrigins, platform],
  );

  return {
    processUrl,
    isNonProduction: shouldReplace,
  };
}
